import React, { useState, useMemo } from "react";
import { NavLink, useParams, Link } from "react-router-dom";
import { useContactModal } from "../context/ContactModalContext";
import ProductCard from "../components/ProductCard";
import ImageTile from "../components/ImageTile";
import { categories, findCategory } from "../data/products";
import { heroImages } from "../data/images";

export default function ProductsPage() {
  const { category } = useParams();
  const active = category ? findCategory(category) : null;
  const { openModal } = useContactModal();
  const [search, setSearch] = useState("");
  const [seasonFilter, setSeasonFilter] = useState("all");

  // Flatten + filter for the all-products view
  const filteredCategories = useMemo(() => {
    const list = active ? [active] : categories;
    if (!search && seasonFilter === "all") return list;
    return list
      .map((c) => ({
        ...c,
        items: c.items.filter((i) => {
          const matchesSearch = !search || i.name.toLowerCase().includes(search.toLowerCase()) || (i.note || "").toLowerCase().includes(search.toLowerCase());
          const matchesSeason = seasonFilter === "all"
            || (seasonFilter === "year-round" && /year[- ]round/i.test(i.season || ""))
            || (seasonFilter === "seasonal" && !/year[- ]round/i.test(i.season || ""));
          return matchesSearch && matchesSeason;
        }),
      }))
      .filter((c) => c.items.length > 0);
  }, [active, search, seasonFilter]);

  return (
    <>
      {/* ── PAGE TITLE BANNER (right under navbar) ── */}
      <section className="relative w-full mt-16 md:mt-20 overflow-hidden">
        <div
          className="relative w-full py-16 md:py-20 overflow-hidden"
          style={{
            background:
              "radial-gradient(ellipse at 18% 25%, rgba(107,142,35,0.22), transparent 55%)," +
              "radial-gradient(ellipse at 82% 75%, rgba(45,212,191,0.18), transparent 55%)," +
              "linear-gradient(135deg, #E8EFD9 0%, #DCE7C8 35%, #D4E2BD 70%, #C9DBB0 100%)",
          }}
        >
          {/* Subtle dot grid texture */}
          <div className="absolute inset-0 opacity-[0.10] pointer-events-none"
            style={{ backgroundImage: "radial-gradient(rgba(61,61,51,0.5) 1px, transparent 1px)", backgroundSize: "20px 20px" }} />

          {/* Soft glow blobs */}
          <div className="absolute -top-20 -left-10 w-96 h-96 rounded-full blur-3xl opacity-40 pointer-events-none"
            style={{ background: "radial-gradient(circle, rgba(134,239,172,0.45), transparent 70%)" }} />
          <div className="absolute -bottom-24 right-0 w-md h-112 rounded-full blur-3xl opacity-30 pointer-events-none"
            style={{ background: "radial-gradient(circle, rgba(107,142,35,0.40), transparent 70%)" }} />

          <div className="relative w-full px-4 sm:px-6 lg:px-10 max-w-400 mx-auto text-center fade-up">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white/60 border border-emerald-700/15 backdrop-blur-sm mb-5">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-600" />
              <span className="text-[11px] uppercase tracking-[0.22em] text-emerald-800 font-medium">
                {active ? `${active.label} · Export Catalog` : "Our Produce"}
              </span>
            </div>
            <h1 className="font-display text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-semibold tracking-tight text-stone-900 leading-[1.02] uppercase">
              {active ? active.label : "All Products"}
            </h1>
            <nav className="mt-6 flex items-center justify-center gap-2 text-sm text-stone-600">
              <Link to="/" className="hover:text-emerald-700 transition">Home</Link>
              <span className="text-stone-400">/</span>
              <Link to="/products" className="hover:text-emerald-700 transition">Products</Link>
              {active && (
                <>
                  <span className="text-stone-400">/</span>
                  <span className="text-stone-900 font-medium">{active.label}</span>
                </>
              )}
            </nav>
          </div>
        </div>
      </section>

      {/* ── STATS STRIP ── */}
      <section className="relative bg-stone-900 border-y border-stone-800">
        <div className="w-full px-4 sm:px-6 lg:px-10 max-w-400 mx-auto">
          <div className="grid grid-cols-2 md:grid-cols-4 divide-x divide-stone-800">
            {[
              { stat: "25+", label: "Export Destinations" },
              { stat: "100+", label: "SKUs in Catalog" },
              { stat: "4", label: "Product Families" },
              { stat: "100%", label: "Farm Traceable" },
            ].map((s, i) => (
              <div key={i} className="px-6 py-6 text-center md:text-left">
                <div className="font-display text-3xl md:text-4xl font-semibold text-emerald-300">{s.stat}</div>
                <div className="text-[11px] uppercase tracking-widest text-stone-400 mt-1">{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── MAIN: SIDEBAR + GRID ── */}
      <section className="relative py-12 md:py-16">
        <div className="w-full px-4 sm:px-6 lg:px-10 max-w-400 mx-auto">
          <div className="grid lg:grid-cols-12 gap-8">

            {/* SIDEBAR FILTER */}
            <aside className="lg:col-span-3">
              <div className="lg:sticky lg:top-28 space-y-6">

                {/* Search */}
                <div className="rounded-2xl bg-white border border-stone-900/10 p-5">
                  <div className="text-[10px] uppercase tracking-[0.22em] text-stone-400 font-medium mb-3">Search</div>
                  <div className="relative">
                    <svg viewBox="0 0 24 24" className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" fill="none" stroke="currentColor" strokeWidth="2">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-4.35-4.35M11 19a8 8 0 110-16 8 8 0 010 16z" />
                    </svg>
                    <input
                      type="text"
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                      placeholder="Search products..."
                      className="w-full text-sm text-stone-800 pl-9 pr-3 py-2.5 rounded-xl border border-stone-900/10 bg-stone-50/50 focus:border-emerald-600 focus:bg-white outline-none transition"
                    />
                  </div>
                </div>

                {/* Category list */}
                <div className="rounded-2xl bg-white border border-stone-900/10 overflow-hidden">
                  <div className="px-5 py-4 border-b border-stone-900/8 bg-stone-50/50">
                    <div className="text-[10px] uppercase tracking-[0.22em] text-stone-400 font-medium">Categories</div>
                  </div>
                  <ul className="divide-y divide-stone-900/8">
                    <li>
                      <NavLink to="/products" end
                        className={({ isActive }) => `flex items-center justify-between px-5 py-3.5 text-sm transition ${isActive ? "bg-emerald-50/60 text-emerald-800 font-medium border-l-2 border-emerald-600" : "text-stone-700 hover:bg-stone-50/60"}`}>
                        <span>All Products</span>
                        <span className="text-[11px] text-stone-400">{categories.reduce((acc, c) => acc + c.items.length, 0)}</span>
                      </NavLink>
                    </li>
                    {categories.map((c) => (
                      <li key={c.key}>
                        <NavLink to={`/products/${c.key}`}
                          className={({ isActive }) => `flex items-center justify-between px-5 py-3.5 text-sm transition ${isActive ? "bg-emerald-50/60 text-emerald-800 font-medium border-l-2 border-emerald-600" : "text-stone-700 hover:bg-stone-50/60"}`}>
                          <span className="flex items-center gap-2.5">
                            <span className="text-base">{c.emoji}</span>
                            {c.label}
                          </span>
                          <span className="text-[11px] text-stone-400">{c.items.length}</span>
                        </NavLink>
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Season filter */}
                <div className="rounded-2xl bg-white border border-stone-900/10 p-5">
                  <div className="text-[10px] uppercase tracking-[0.22em] text-stone-400 font-medium mb-3">Availability</div>
                  <div className="space-y-2">
                    {[
                      { key: "all", label: "All seasons" },
                      { key: "year-round", label: "Year-round" },
                      { key: "seasonal", label: "Seasonal" },
                    ].map((opt) => (
                      <label key={opt.key} className="flex items-center gap-2.5 cursor-pointer text-sm text-stone-700 hover:text-stone-900">
                        <input
                          type="radio"
                          name="season"
                          checked={seasonFilter === opt.key}
                          onChange={() => setSeasonFilter(opt.key)}
                          className="accent-emerald-600"
                        />
                        {opt.label}
                      </label>
                    ))}
                  </div>
                </div>

                {/* Inquiry CTA card */}
                <div className="rounded-2xl bg-stone-900 text-white p-6 overflow-hidden relative">
                  <div className="absolute -top-10 -right-10 w-32 h-32 rounded-full bg-emerald-600/20 blur-3xl" />
                  <div className="relative">
                    <div className="text-[10px] uppercase tracking-[0.22em] text-emerald-300 font-medium mb-2">Bulk Buyer?</div>
                    <div className="font-display text-lg font-medium leading-tight">Get volume pricing in 24 hours</div>
                    <p className="text-xs text-stone-400 mt-2 leading-relaxed">Tell us your target market and volume — we'll respond with a tailored offer.</p>
                    <button onClick={openModal}
                      className="mt-4 w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium text-stone-900 bg-emerald-300 hover:bg-emerald-200 transition">
                      Request Quote
                      <svg viewBox="0 0 24 24" className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth="2.5">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M5 12h14M13 5l7 7-7 7" />
                      </svg>
                    </button>
                  </div>
                </div>
              </div>
            </aside>

            {/* PRODUCT LISTING */}
            <div className="lg:col-span-9">
              {/* Listing toolbar */}
              <div className="flex items-center justify-between mb-6 pb-5 border-b border-stone-900/10">
                <div>
                  <div className="text-[11px] uppercase tracking-[0.22em] text-stone-400 font-medium">
                    {active ? active.label : "All Products"}
                  </div>
                  <div className="font-display text-xl text-stone-900 mt-0.5">
                    {filteredCategories.reduce((acc, c) => acc + c.items.length, 0)} products available
                  </div>
                </div>
                <div className="hidden md:flex items-center gap-3 text-xs text-stone-500">
                  <span>Sort:</span>
                  <select className="text-sm text-stone-800 px-3 py-2 rounded-lg border border-stone-900/12 bg-white outline-none focus:border-emerald-600 transition">
                    <option>Featured</option>
                    <option>Name (A–Z)</option>
                    <option>Season</option>
                  </select>
                </div>
              </div>

              {/* Empty state */}
              {filteredCategories.length === 0 && (
                <div className="rounded-2xl border border-stone-900/10 bg-white p-12 text-center">
                  <div className="text-5xl mb-3">🔍</div>
                  <div className="font-display text-xl text-stone-900">No products match your search</div>
                  <p className="text-sm text-stone-500 mt-2">Try clearing the filter or contact us for custom sourcing.</p>
                  <button onClick={openModal}
                    className="mt-5 inline-flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-medium bg-stone-900 text-white hover:bg-stone-800 transition">
                    Submit a Custom Inquiry
                  </button>
                </div>
              )}

              {/* Listing — single category view OR grouped all-products view */}
              {active ? (
                <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-5">
                  {filteredCategories[0]?.items.map((item) => (
                    <ProductCard key={item.slug} item={item} accent={active.accent} categoryKey={active.key} />
                  ))}
                </div>
              ) : (
                filteredCategories.map((c) => (
                  <div key={c.key} className="mb-14 last:mb-0">
                    <div className="flex items-end justify-between gap-4 mb-6">
                      <div>
                        <div className="text-[11px] uppercase tracking-[0.22em] text-stone-400 font-medium mb-1">Category</div>
                        <h2 className="font-display text-2xl md:text-3xl font-semibold text-stone-900">
                          <span className="mr-2">{c.emoji}</span>{c.label}
                        </h2>
                      </div>
                      <Link to={`/products/${c.key}`}
                        className="text-sm text-emerald-700 font-medium hover:underline whitespace-nowrap">
                        View all {c.items.length} →
                      </Link>
                    </div>
                    <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-5">
                      {c.items.slice(0, 6).map((item) => (
                        <ProductCard key={item.slug} item={item} accent={c.accent} categoryKey={c.key} />
                      ))}
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      </section>

      {/* ── EDITORIAL: WHY SOURCE WITH US — alternating image+text ── */}
      <section className="relative py-16 md:py-24 bg-white/40 border-y border-stone-900/8">
        <div className="w-full px-4 sm:px-6 lg:px-10 max-w-400 mx-auto">
          <div className="grid lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-5">
              <div className="rounded-3xl overflow-hidden border border-stone-900/10 aspect-4/5">
                <ImageTile src={heroImages.harvestHands} alt="" emoji="🌾"
                  className="w-full h-full" overlayClassName="bg-black/15" />
              </div>
            </div>
            <div className="lg:col-span-7">
              <div className="text-[11px] uppercase tracking-[0.22em] text-emerald-700 font-medium mb-3">Why source with us</div>
              <h2 className="font-display text-3xl md:text-5xl font-semibold text-stone-900 leading-[1.05]">
                More than a supplier — <span className="text-gradient">a partner.</span>
              </h2>
              <p className="mt-5 text-stone-600 leading-relaxed text-base md:text-lg max-w-xl">
                Every consignment passes through the same uncompromising chain: certified farms, pack-house grading, pre-cooling, and reefer transit — owned end to end. No middlemen, no surprises.
              </p>

              <div className="mt-8 grid sm:grid-cols-2 gap-x-8 gap-y-5">
                {[
                  { title: "Direct from farm", text: "No middlemen — buying clusters across Maharashtra, Gujarat, Karnataka." },
                  { title: "Cold chain integrity", text: "Pre-cooled within hours of harvest, controlled until the discharge port." },
                  { title: "Documented compliance", text: "FSSAI, APEDA, Phytosanitary, EU MRL — paperwork ready before sailing." },
                  { title: "Buyer-flexible packing", text: "Mesh, jute, cartons, vacuum, IQF — packed to your spec, your label." },
                ].map((v, i) => (
                  <div key={i} className="flex items-start gap-3 fade-up" style={{ animationDelay: `${i * 0.05}s` }}>
                    <div className="shrink-0 w-7 h-7 rounded-lg bg-emerald-100 border border-emerald-600/20 flex items-center justify-center mt-0.5">
                      <svg viewBox="0 0 24 24" className="w-4 h-4 text-emerald-700" fill="none" stroke="currentColor" strokeWidth="2.5">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                    <div>
                      <div className="font-display text-base font-medium text-stone-900">{v.title}</div>
                      <p className="text-sm text-stone-600 mt-1 leading-relaxed">{v.text}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── PROCESS BAR — horizontal numbered timeline ── */}
      <section className="relative py-16 md:py-20">
        <div className="w-full px-4 sm:px-6 lg:px-10 max-w-400 mx-auto">
          <div className="text-center max-w-3xl mx-auto mb-14">
            <div className="text-[11px] uppercase tracking-[0.22em] text-emerald-700 font-medium mb-3">From Inquiry to Port</div>
            <h2 className="font-display text-3xl md:text-5xl font-semibold text-stone-900 leading-[1.05]">
              How we <span className="text-gradient">deliver.</span>
            </h2>
          </div>

          <div className="relative">
            {/* Connecting line */}
            <div className="hidden lg:block absolute top-7 left-[10%] right-[10%] h-px bg-linear-to-r from-transparent via-stone-300 to-transparent" />

            <div className="grid grid-cols-2 lg:grid-cols-5 gap-6 lg:gap-3">
              {[
                { step: "01", title: "Inquiry", text: "Share product, volume, port, timeline." },
                { step: "02", title: "Quotation", text: "Tailored offer within 1 business day." },
                { step: "03", title: "Sourcing", text: "Cluster farms, lot-tagged for traceability." },
                { step: "04", title: "Pack & Inspect", text: "Grading, pre-cooling, third-party audit." },
                { step: "05", title: "Dispatch", text: "Reefer booked, documents released." },
              ].map((p, i) => (
                <div key={i} className="relative text-center fade-up" style={{ animationDelay: `${i * 0.06}s` }}>
                  <div className="relative inline-flex items-center justify-center w-14 h-14 rounded-full bg-white border-2 border-emerald-600 text-emerald-700 font-semibold font-display mb-4">
                    {p.step}
                  </div>
                  <div className="font-display text-lg font-medium text-stone-900">{p.title}</div>
                  <p className="text-sm text-stone-600 mt-2 leading-relaxed max-w-50 mx-auto">{p.text}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── BOTTOM CTA — dark band ── */}
      <section className="relative bg-stone-900">
        <div className="w-full px-4 sm:px-6 lg:px-10 max-w-400 mx-auto py-14 md:py-16">
          <div className="grid md:grid-cols-2 gap-6 items-center">
            <div>
              <div className="text-[11px] uppercase tracking-[0.22em] text-emerald-300 font-medium mb-2">Custom Sourcing</div>
              <h3 className="font-display text-3xl md:text-4xl font-semibold text-white leading-tight">
                Need a product not <span className="text-emerald-300">listed here?</span>
              </h3>
              <p className="mt-3 text-stone-400 max-w-md">Our network extends well beyond this catalog. Tell us what you need — chances are, we can supply it.</p>
            </div>
            <div className="md:text-right flex md:justify-end gap-3 flex-wrap">
              <button onClick={openModal}
                className="inline-flex items-center gap-2 px-7 py-3.5 rounded-xl text-sm font-medium text-stone-900 bg-emerald-300 hover:bg-emerald-200 transition">
                Submit Custom Inquiry
                <svg viewBox="0 0 24 24" className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M5 12h14M13 5l7 7-7 7" />
                </svg>
              </button>
              <a href="tel:+919876543210"
                className="inline-flex items-center gap-2 px-7 py-3.5 rounded-xl text-sm font-medium text-white border border-white/20 hover:bg-white/5 transition">
                Call us
              </a>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
